import { useState } from "react";
import { toast } from "@/lib/toast";
import { Modal } from "@/components/ui-z";

// Documentação da integração de entrada (POST /api/webhooks/inbound/{token}), com exemplos em cURL.
// Os exemplos já usam a URL da API e o token da integração selecionada, quando houver
const EXTENSOES = ".png .jpg .jpeg .gif .webp .pdf .zip .docx .xlsx .txt .log .csv .json .xml";

const CAMPOS = [
  ["titulo", "subject", "texto", "Título do chamado. Sem ele: \"Chamado via <plataforma>\"."],
  ["descricao", "message, text", "texto", "Descrição do problema."],
  ["solicitante", "from, sender", "texto", "Nome de quem abriu. Sem ele: \"Desconhecido\"."],
  ["contato", "phone, email", "texto", "Telefone ou e-mail do solicitante."],
  ["prioridade", "", "texto", "baixa, media, alta ou critica. Ausente ou inválida: media."],
  ["produto_id", "", "número", "Id do produto (GET /api/produtos, cadastro em Customização). Inválido ou inativo: 400."],
  ["categoria", "", "texto", "Sem produto_id: texto livre. Se for o nome de um produto ativo, o chamado fica com esse produto."],
  ["conversation_id", "chat_id, thread_id", "texto", "Referência da conversa de origem (aparece em \"Conversa de origem\")."],
  ["evidencias_ids", "", "lista de ids", "Ids devolvidos pelo envio de arquivos (passo 1). Até 10 por chamado."],
];

const ERROS = [
  ["404", "Webhook inválido", "Token errado ou integração removida."],
  ["400", "Arquivos inválidos", "Algum arquivo foi recusado no envio; nenhum foi guardado. A lista \"erros\" diz o motivo de cada um."],
  ["400", "Produto inválido ou inativo", "O produto_id não existe ou foi inativado na Customização. O chamado não é criado."],
  ["400", "Evidências inválidas", "Algum id não existe, já foi usado, expirou (24h) ou é de outra integração. O chamado não é criado."],
];

const Code = ({ children, testId }) => (
  <div className="position-relative mb-3">
    <button type="button" className="btn btn-xs btn-default position-absolute" style={{ top: 6, right: 6 }} title="Copiar"
      onClick={() => { navigator.clipboard?.writeText(children); toast("Copiado"); }}>
      <i className="fas fa-copy" />
    </button>
    <pre className="bg-dark text-light rounded p-3 mb-0 text-sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }} data-testid={testId}>{children}</pre>
  </div>
);

export default function ApiDocsModal({ baseUrl, token, onClose }) {
  const [shell, setShell] = useState("bash");
  const url = `${baseUrl}${token || "{token}"}`;
  const ps = shell === "powershell";

  // bash/Git Bash: JSON inline entre aspas simples, quebra de linha com "\".
  // PowerShell: "curl.exe" (o "curl" de lá é outro comando), quebra com "`" e JSON num arquivo UTF-8,
  // porque o Windows PowerShell 5.1 tira as aspas do JSON ao passar o argumento para o curl.exe
  const post = (obj) => {
    const body = JSON.stringify(obj, null, 2);
    return ps
      ? `@'\n${body}\n'@ | Out-File chamado.json -Encoding utf8\n\ncurl.exe -X POST "${url}" \`\n  -H "Content-Type: application/json" \`\n  --data-binary "@chamado.json"`
      : `curl -X POST "${url}" \\\n  -H "Content-Type: application/json" \\\n  -d '${body}'`;
  };
  const upload = ps
    ? `curl.exe -X POST "${url}/files" \`\n  -F "files=@print.png" \`\n  -F "files=@log.txt"`
    : `curl -X POST "${url}/files" \\\n  -F "files=@print.png" \\\n  -F "files=@log.txt"`;
  const criar = post({ solicitante: "Maria Souza", contato: "maria@empresa.com", titulo: "Erro 422 ao enviar termo", descricao: "Ao enviar o termo de cessão na ZiSign aparece o erro 422.", prioridade: "alta", categoria: "ZiSign", conversation_id: "wa-chat-8812", evidencias_ids: ["<id do print.png>", "<id do log.txt>"] });
  const semArquivo = post({ solicitante: "João", titulo: "Sem acesso ao portal", descricao: "Usuário bloqueado desde hoje cedo." });

  return (
    <Modal testId="api-docs-modal" size="modal-xl" onClose={onClose}
      title={<><i className="fas fa-book mr-2" />Como integrar: abrir chamados pela API</>}
      footer={<><span className="text-muted text-sm">{token ? "Os exemplos já usam o token da integração selecionada." : "Selecione uma integração para os exemplos virem com o token dela."}</span><button type="button" className="btn btn-default" data-dismiss="modal">Fechar</button></>}>

      <p>
        Cada integração tem um <b>token</b> próprio (a parte final do endpoint). Não é preciso login: quem tem o token consegue abrir chamados,
        então guarde-o como senha. Abrir um chamado com arquivos leva <b>dois passos</b>: primeiro os arquivos são enviados e a API devolve um
        <b> id</b> para cada um; depois o chamado é criado citando esses ids. Assim o JSON do chamado fica pequeno, sem o conteúdo dos arquivos.
      </p>

      <div className="d-flex align-items-center mb-3">
        <span className="text-muted text-sm mr-2">Exemplos para</span>
        <div className="btn-group btn-group-sm" role="group">
          <button type="button" className={`btn btn-default ${ps ? "" : "active"}`} onClick={() => setShell("bash")}>Linux / macOS / Git Bash</button>
          <button type="button" className={`btn btn-default ${ps ? "active" : ""}`} onClick={() => setShell("powershell")}>PowerShell</button>
        </div>
      </div>

      <h5><span className="badge badge-primary mr-2">1</span>Enviar os arquivos <small className="text-muted">(opcional)</small></h5>
      <p className="mb-2">
        <code>POST {url}/files</code> com <code>multipart/form-data</code>, um campo <code>files</code> por arquivo.
        Rode o comando na pasta dos arquivos (ou use o caminho completo depois do <code>@</code>).
      </p>
      <Code testId="docs-curl-upload">{upload}</Code>
      <p className="mb-2">Resposta: guarde o <code>id</code> de cada arquivo.</p>
      <Code>{`{
  "arquivos": [
    { "id": "579f017c-a0b4-402c-aa42-09baab3f5a3b", "name": "print.png", "content_type": "image/png", "size": 48213, "expires_at": "2026-10-20T21:11:41Z" },
    { "id": "dac008a0-6099-4c97-b072-ab8de0d9b3dd", "name": "log.txt", "content_type": "text/plain", "size": 1920, "expires_at": "2026-10-20T21:11:41Z" }
  ]
}`}</Code>
      <div className="callout callout-info text-sm">
        <ul className="mb-0 pl-3">
          <li>Até <b>5 arquivos</b> por envio e <b>10 MB</b> por arquivo.</li>
          <li>Extensões aceitas: <code>{EXTENSOES}</code>.</li>
          <li>O conteúdo é conferido com a extensão: um <code>.exe</code> renomeado para <code>.png</code>, por exemplo, é recusado. Arquivos de texto precisam ser UTF-8.</li>
          <li><b>Tudo ou nada:</b> se um arquivo for inválido, nenhum do envio é guardado.</li>
          <li>Cada id vale por <b>24 horas</b>, só para esta integração e só uma vez.</li>
        </ul>
      </div>

      <h5 className="mt-4"><span className="badge badge-primary mr-2">2</span>Criar o chamado</h5>
      <p className="mb-2"><code>POST {url}</code> com JSON. Os ids do passo 1 vão em <code>evidencias_ids</code>.</p>
      <Code testId="docs-curl-create">{criar}</Code>
      <p className="mb-2">Resposta:</p>
      <Code>{`{ "ok": true, "ticket_codigo": "ZTC-10009", "ticket_id": "525bf7aa-6b86-4618-85a2-e244cc8ad9d6", "evidencias": 2 }`}</Code>
      <p className="mb-2">Sem arquivos, é só o passo 2, sem <code>evidencias_ids</code>:</p>
      <Code>{semArquivo}</Code>
      {!ps && <p className="text-muted text-sm">No Git Bash do Windows, acentos passados direto no <code>-d</code> podem chegar trocados. Nesse caso, salve o JSON num arquivo UTF-8 e use <code>--data-binary "@chamado.json"</code>.</p>}
      {ps && <p className="text-muted text-sm">No PowerShell o JSON vai num arquivo (<code>chamado.json</code>, em UTF-8): passado direto na linha de comando, o Windows PowerShell tira as aspas e a API recebe um JSON inválido. O arquivo também garante os acentos.</p>}

      <h5 className="mt-4">Campos do chamado</h5>
      <div className="table-responsive">
        <table className="table table-sm table-bordered text-sm">
          <thead><tr><th>Campo</th><th>Também aceito como</th><th>Tipo</th><th>Descrição</th></tr></thead>
          <tbody>{CAMPOS.map(([c, alt, tipo, desc]) => <tr key={c}><td><code>{c}</code></td><td>{alt && <code>{alt}</code>}</td><td>{tipo}</td><td>{desc}</td></tr>)}</tbody>
        </table>
      </div>
      <p className="text-muted text-sm">Todos os campos são opcionais. O chamado entra na etapa de Triagem, com origem igual à plataforma da integração.</p>

      <h5 className="mt-4">Erros</h5>
      <div className="table-responsive">
        <table className="table table-sm table-bordered text-sm mb-2">
          <thead><tr><th>Status</th><th>detail</th><th>Quando acontece</th></tr></thead>
          <tbody>{ERROS.map(([s, d, q]) => <tr key={d}><td><span className={`badge ${s === "404" ? "badge-warning" : "badge-danger"}`}>{s}</span></td><td><code>{d}</code></td><td>{q}</td></tr>)}</tbody>
        </table>
      </div>
      <Code>{`{
  "detail": "Arquivos inválidos",
  "erros": [ { "name": "virus.png", "erro": "O conteúdo do arquivo não corresponde à extensão." } ]
}`}</Code>
    </Modal>
  );
}
